import { BehaviorSubject, Observable, asapScheduler } from 'rxjs';
import { observeOn } from 'rxjs/operators';

// Type for any constructor (concrete or abstract)
type AnyConstructor<T = {}> =
  | (new (...args: any[]) => T)
  | (abstract new (...args: any[]) => T);

// Interface that the mixin adds
export interface WithTooltipState {
  tooltip$: Observable<string | undefined | null>;
  setTooltip(tooltip: string | undefined | null): void;
}

/**
 * Mixin that adds reactive tooltip state management to a component.
 * This avoids ExpressionChangedAfterItHasBeenChecked errors by:
 * 1. Using a BehaviorSubject for tooltip state
 * 2. Using asapScheduler to defer emissions to the next microtask
 *
 * Works with both concrete and abstract base classes.
 *
 * Usage:
 * ```typescript
 * class MyComponent extends withTooltipState(BaseComponent) {
 *   protected override initDisabledObservable(): void {
 *     this.isDisabled$ = this.someSource$.pipe(
 *       map((data) => {
 *         // Instead of: this.action.tooltip = 'some tooltip';
 *         // Use: this.setTooltip('some tooltip');
 *         this.setTooltip(computedTooltip);
 *         return isDisabled;
 *       })
 *     );
 *   }
 * }
 * ```
 */
export function withTooltipState<TBase extends AnyConstructor>(Base: TBase) {
  abstract class WithTooltip extends Base implements WithTooltipState {
    private _tooltip$ = new BehaviorSubject<string | undefined | null>(
      undefined
    );

    /**
     * Observable that emits tooltip values on the asapScheduler.
     * This ensures emissions happen in a separate microtask, avoiding
     * ExpressionChangedAfterItHasBeenChecked errors.
     */
    tooltip$ = this._tooltip$.asObservable().pipe(observeOn(asapScheduler));

    setTooltip(tooltip: string | undefined | null): void {
      // Only emit if value changed to avoid unnecessary updates
      if (this._tooltip$.value !== tooltip) {
        this._tooltip$.next(tooltip);
      }
    }
  }
  return WithTooltip;
}
